<template>
    <Modal :modal-title="modalTitle"
           :dialog="dialog"
           max-width="1600px"
           data-test="modal"
           @close="close"
           @visibility="visibility($event)">
        <template v-slot:actions>
            <template v-if="canDownloadPdf">
                <SendMail :dialog="sendMailVisibility"
                          :callback="sendIncidentMail"
                          :default-title="`${$t('labels.incident_details')} ${activeIncident.id}`"
                          @close="closeSendMail"
                          @visibility="sendMailVisibility = $event"></SendMail>

                <v-btn color="secondary"
                       @click="showSendMail">
                        <v-icon left size="20">{{ ICONS.EMAIL_SEND }}</v-icon>
                    {{ $t('buttons.send_mail') }}
                </v-btn>
                <GetFile :loader="pdfLoader"
                        @get-file="getPdf"></GetFile>
            </template>
        </template>
        <template v-slot:content>
            <v-row>
                <v-col cols="12">
                    <h3 class="font-weight-medium">{{ $t('labels.details') }}</h3>
                    <v-row>
                        <v-col md="8"
                               sm="12"
                               cols="12">
                            <v-sheet color="white"
                                     class="pa-4"
                                     elevation="1">
                                <v-layout align-stretch
                                          :wrap="isMobile">
                                    <v-flex sm6
                                            :class="{'bordered pr-4': smAndUp}">
                                        <InformationItem :title="$t('labels.customer')"
                                                         @edit="openEditForm()"
                                                         data-test="textShowIncidentFormButton"
                                                         name="customer"
                                                         :text="activeIncident.customer_name"></InformationItem>
                                        <InformationItem :title="$t('labels.site')"
                                                         @edit="openEditForm()"
                                                         name="site"
                                                         :text="activeIncident.place_name"></InformationItem>
                                        <InformationItem :title="$t('labels.location')"
                                                         @edit="openEditForm()"
                                                         name="location"
                                                         :text="activeIncident.point_name"></InformationItem>
                                        <InformationItem :title="$t('labels.location_time_from')"
                                                         @edit="openEditForm()"
                                                         name="incidents_date"
                                                         :text="activeIncident.parsed_local_start_date_time">
                                        </InformationItem>
                                        <InformationItem :title="$t('labels.location_time_to')"
                                                         @edit="openEditForm()"
                                                         name="end_date"
                                                         :text="activeIncident.parsed_local_end_date_time">
                                        </InformationItem>
                                    </v-flex>
                                    <v-flex sm6
                                            :class="{'bordered pl-4': smAndUp}">
                                        <InformationItem :title="$t('labels.incident_type')"
                                                         @edit="openEditForm()"
                                                         name="type">
                                                <Type v-if="activeIncident.incidentType"
                                                      :type="activeIncident.incidentType"></Type>
                                        </InformationItem>
                                        <InformationItem :title="$t('labels.status')"
                                                         @edit="openEditForm()"
                                                         name="status">
                                                <Status v-if="activeIncident.incidentStatus"
                                                        :status="activeIncident.incidentStatus"></Status>
                                        </InformationItem>
                                        <InformationItem :title="$t('labels.created_by')"
                                                         @edit="openEditForm()"
                                                         name="person"
                                                         :text="activeIncident.employee_name"></InformationItem>
                                        <InformationItem :title="$t('labels.responsible_person')"
                                                         @edit="openEditForm()"
                                                         name="person_assigned"
                                                         :text="activeIncident.responsible_employee_name"></InformationItem>
                                        <InformationItem :title="$t('labels.description')"
                                                         @edit="openEditForm()"
                                                         name="description"
                                                         :text="activeIncident.description"></InformationItem>
                                    </v-flex>
                                </v-layout>
                                <v-layout justify-end>
                                    <template v-if="showSourceInspection">
                                        <v-btn color="secondary"
                                               v-if="activeInspection"
                                               class="mr-3"
                                               @click="showInspectionDetails">{{ $t('labels.inspection_details') }}
                                        </v-btn>
                                        <InspectionDetails v-if="activeInspection"
                                                           @close="closeInspectionDetails"
                                                           :dialog="inspectionDetailsVisibility"
                                                           @visibility="inspectionDetailsVisibility = $event"></InspectionDetails>
                                    </template>
                                    <v-btn color="secondary"
                                           v-if="canEditIncident"
                                           @click="openEditForm()">
                                        <v-icon left size="20">{{ ICONS.EDIT }}</v-icon>
                                        {{ $t('buttons.edit') }}
                                    </v-btn>
                                </v-layout>
                            </v-sheet>
                            <div class="mt-6" v-if="canEditIncident">
                                <h3 class="font-weight-medium mb-3">
                                    {{ $t('labels.incident_set_status') }}
                                    <CircularLoader class="ml-3" v-if="loader" />
                                </h3>
                                <v-sheet color="white" class="mt-3" elevation="1">
                                    <v-slide-group class="px-4 mr-auto" show-arrows>
                                        <v-slide-item
                                            v-for="incidentStatus in incidentStatuses"
                                            :key="incidentStatus.id"
                                            class="statusItem my-4"
                                        >
                                            <v-btn
                                                color="secondary"
                                                :outlined="!isStatusActive(incidentStatus)"
                                                @click="changeStatus(incidentStatus)"
                                               :disabled="loader"
                                            >
                                                <Type :type="getStatus(incidentStatus)" /></v-btn>
                                        </v-slide-item>

                                    </v-slide-group>
                                </v-sheet>
                            </div>
                        </v-col>
                        <v-col md="4"
                               sm="12"
                               cols="12">
                            <Map :lat="activeIncident.latitude"
                                 :lng="activeIncident.longitude"></Map>
                        </v-col>
                    </v-row>
                </v-col>

                <v-col cols="12">
                    <v-row>
                        <v-col md="8"
                               sm="12"
                               cols="12">
                            <h3 class="font-weight-medium mb-3">{{ $tc('labels.question', 2) }}</h3>
                            <QuestionsTable :question="sortedQuestions"
                                            @load-more-items="getInspectionAnswersLazyLoading($event)"></QuestionsTable>
                        </v-col>
                        <v-col md="4"
                               sm="12"
                               cols="12">
                            <h3 class="font-weight-medium mb-3">{{ $t('labels.photos') }}</h3>
                            <div class="photosWrapper mb-3" v-if="!photosAreLoading">
                                <div v-for="image in activeIncidentPhotos" :key="image.id" class="photo">
                                    <PreviewImage :src="image.content"></PreviewImage>
                                </div>
                            </div>
                            <h3 class="font-weight-medium mb-3">{{ $t('labels.documents') }}</h3>
                            <div class="documentsWrapper" v-if="!photosAreLoading">
                                <div v-for="document in activeIncidentDocuments" :key="document.id" class="document">
                                    <a class="font-weight-bold" :href="document.content" download>{{ document.filename }}</a>
                                </div>
                            </div>
                            <CircularLoader v-else />
                        </v-col>
                    </v-row>
                </v-col>

                <v-col cols="12">
                    <v-row>
                        <v-col md="8"
                               sm="12"
                               cols="12">
                            <h3 class="font-weight-medium mb-3">{{ $tc('labels.comment', 2) }}</h3>
                            <CommentsList></CommentsList>
                        </v-col>
                        <v-col md="4"
                               sm="12"
                               cols="12">
                            <h3 class="font-weight-medium mb-3">{{ $t('labels.statuses_log') }}</h3>
                            <StatusesLog :log="incidentHistory"></StatusesLog>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </template>
    </Modal>
</template>

<script>
import { mapState } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import Modal from '@/components/templates/Modal';
import InformationItem from '@/components/customers/InformationItem';
import Map from '@/components/widgets/Map';
import PreviewImage from '@/components/widgets/PreviewImage';
import StatusesLog from '@/components/incidents/StatusesLog';
import QuestionsTable from '@/components/questions/QuestionsTable';
import CommentsList from '@/components/incidents/CommentsList';
import InspectionDetails from '@/components/inspections/InspectionDetails';
import Type from '@/components/widgets/Type';
import Status from '@/components/widgets/Status';
import GetFile from '@/components/widgets/buttons/GetFile';
import SendMail from '@/components/widgets/dialogs/SendMail';
import { showBase64Document } from '@/helpers/files';
import CircularLoader from '@/components/widgets/CircularLoader.vue';
import { ActionStatus } from '@/constants/status';
import debouncedIncidentStatuses from '@/mixins/debounced/debouncedIncidentStatusesV2';
import { ICONS } from '@/constants/icons';

const BUTTON_TEXT_LENGTH = 15;

export default {
    name: 'IncidentDetails',
    components: {
        CircularLoader,
        Modal,
        InformationItem,
        Map,
        PreviewImage,
        StatusesLog,
        QuestionsTable,
        CommentsList,
        Type,
        Status,
        GetFile,
        SendMail,
        InspectionDetails
    },
    mixins: [
        debouncedIncidentStatuses
    ],
    props: {
        dialog: {
            type: Boolean,
            required: true
        },
        isEditable: {
            type: Boolean,
            'default': false
        }
    },
    data: () => ({
        pagination: {
            page: 1,
            amount: 20
        },
        pdfLoader: false,
        sendMailVisibility: false,
        inspectionDetailsVisibility: false,
        photosAreLoading: false,
        documentsAreLoading: false,
        loader: false,
        ICONS
    }),
    computed: {
        ...mapState('manageIncident', [
            'activeIncident',
            'activeIncidentPhotos',
            'activeIncidentPhotosStatus',
            'activeIncidentDocuments',
            'activeIncidentDocumentsStatus',
            'incidentHistory',
            'incidentComments'
        ]),
        ...mapState('manageInspection', [
            'inspectionQuestions',
            'activeInspection'
        ]),
        ...mapFields('manageIncident', [
            'incidentFormVisibility',
            'editedIncidentId'
        ]),
        ...mapState('incidents', [
            'incidentStatuses'
        ]),
        modalTitle () {
            return this.$t('labels.incident') + ' ' + this.activeIncident?.id;
        },
        isMobile () {
            return this.$vuetify.breakpoint.xs;
        },
        smAndUp () {
            return this.$vuetify.breakpoint.smAndUp;
        },
        canEditIncident () {
            return this.$can(this.$abilityActions.UPDATE, this.$abilitySubjects.INCIDENT) && this.isEditable;
        },
        canDownloadPdf () {
            return this.$can(this.$abilityActions.GET_REPORT_PDF, this.$abilitySubjects.INCIDENT);
        },
        showSourceInspection () {
            return this.activeInspection &&
                this.$can(this.$abilityActions.READ, this.$abilitySubjects.INSPECTION);
        },
        sortedQuestions () {
            return [...this.inspectionQuestions].sort((a, b) => (a.sequence === -1 || b.sequence === -1) ? -Infinity : a.sequence - b.sequence);
        },
        statusParams () {
            return {
                'filter[incident_kind_id]': this.activeIncident.incidentType.id
            };
        }
    },
    watch: {
        activeIncidentPhotosStatus: {
            handler (val) {
                this.photosAreLoading = val === ActionStatus.PENDING;
            },
            immediate: true
        },
        activeIncidentDocumentsStatus: {
            handler (val) {
                this.documentsAreLoading = val === ActionStatus.PENDING;
            },
            immediate: true
        }
    },
    methods: {
        getStatuses () {
            return this.getIncidentStatuses(this.statusParams);
        },
        close () {
            this.$emit('close');
        },
        visibility (event) {
            this.$emit('visibility', event);
        },
        openEditForm (item = null) {
            if (!this.canEditIncident) return;

            this.$store.dispatch('manageIncident/getActiveIncident', this.activeIncident.id)
                .then(() => {
                    this.$store.commit('manageIncident/SET_INCIDENT_DATA', { ...this.activeIncident, ...(item || {}) });
                    this.editedIncidentId = this.activeIncident.id;
                    this.incidentFormVisibility = true;
                });
        },
        changeStatus (status) {
            if (!this.canEditIncident || this.isStatusActive(status)) return;
            this.loader = true;

            const payload = {
                incidentStatusId: +status.id,
                incidentsIdsToUpdate: [+this.activeIncident.id]
            };

            return this.$store.dispatch('manageIncident/updateManyStatuses', payload).then(() => {
                this.$store.dispatch('manageIncident/getActiveIncident', this.editedIncidentId);
                this.close();
            }).finally(() => {
                this.loader = false;
            });
        },
        getInspectionAnswers () {
            const payload = {
                id: this.activeIncident.inspectionWithAnswersId,
                page: this.pagination.page,
                amount: this.pagination.amount
            };

            return this.$store.dispatch('manageInspection/getInspectionAnswers', payload);
        },
        getInspectionAnswersLazyLoading (event) {
            this.pagination.page = event;

            const payload = {
                id: this.activeIncident.inspectionWithAnswersId || this.activeIncident.source_inspection_id,
                page: this.pagination.page,
                amount: this.pagination.amount
            };

            return this.$store.dispatch('manageInspection/getInspectionAnswersLazyLoading', payload);
        },
        getPdf () {
            this.pdfLoader = true;

            this.$store.dispatch('manageIncident/getIncidentPDF', this.activeIncident.id)
                .finally(() => {
                    this.pdfLoader = false;
                });
        },
        showSendMail () {
            this.sendMailVisibility = true;
        },
        closeSendMail () {
            this.sendMailVisibility = false;
            this.$store.commit('sendMail/RESET_MAIL_DATA');
        },
        sendIncidentMail () {
            return this.$store.dispatch('sendMail/sendIncidentMail', this.activeIncident.id);
        },
        showBase64Document: showBase64Document,
        showInspectionDetails () {
            this.inspectionDetailsVisibility = true;
        },
        closeInspectionDetails () {
            this.inspectionDetailsVisibility = false;
        },
        getStatus (status) {
            return {
                ...status,
                name: status.name.length > BUTTON_TEXT_LENGTH ? `${status.name.substring(0, BUTTON_TEXT_LENGTH)}...` : status.name
            };
        },
        isStatusActive (incidentStatus) {
            return incidentStatus.id === this.activeIncident.incidentStatus.id;
        }
    },
    created () {
        this.getStatuses();
    }
};
</script>

<style scoped lang="scss">
.photosWrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    .photo {
        display: flex;
        line-height: 0;
    }
}
.statusItem + .statusItem {
    margin-left: 12px;
}

.gap-2 {
    gap: 12px;
}
</style>
